import app from 'flarum/forum/app';
import { extend as flarumExtend } from 'flarum/common/extend';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import UserCard from 'flarum/forum/components/UserCard';
import CommentPost from 'flarum/forum/components/CommentPost';
import UserPage from 'flarum/forum/components/UserPage';
import Link from 'flarum/common/components/Link';
import LinkButton from 'flarum/common/components/LinkButton';
import textContrastClass from 'flarum/common/helpers/textContrastClass';
import SocialGroup from './forum/models/SocialGroup';
import SocialGroupDiscussion from './forum/models/SocialGroupDiscussion';
import SocialGroupPost from './forum/models/SocialGroupPost';
import SocialGroupMember from './forum/models/SocialGroupMember';
import SocialGroupJoinRequest from './forum/models/SocialGroupJoinRequest';
import SocialGroupNewPostNotification from './forum/components/SocialGroupNewPostNotification';
import SocialGroupNewReplyNotification from './forum/components/SocialGroupNewReplyNotification';
import SocialGroupJoinRequestNotification from './forum/components/SocialGroupJoinRequestNotification';
import UserGroupBadges from './forum/components/UserGroupBadges';
app.initializers.add('ernestdefoe-social-groups', app => {
  // flarum/realtime integration is per-discussion now: GroupDiscussionThread
  // subscribes to its group's `private-sg-group.{groupId}` channel on mount
  // so the WebSocket layer enforces the same membership gate the HTTP layer
  // does.  No global public-channel handlers — events delivered there would
  // be visible to every connected client, including non-members.
  app.store.models['social-groups'] = SocialGroup;
  app.store.models['social-group-discussions'] = SocialGroupDiscussion;
  app.store.models['social-group-posts'] = SocialGroupPost;
  app.store.models['social-group-members'] = SocialGroupMember;
  app.store.models['social-group-join-requests'] = SocialGroupJoinRequest;

  // Notification components
  app.notificationComponents.socialGroupNewPost = SocialGroupNewPostNotification;
  app.notificationComponents.socialGroupNewReply = SocialGroupNewReplyNotification;
  app.notificationComponents.socialGroupJoinRequest = SocialGroupJoinRequestNotification;

  // Settings-page notification grid rows (per-user toggles). String-path
  // extend: NotificationGrid ships in an async chunk, so a prototype extend
  // at init time would be a silent no-op.
  flarumExtend('flarum/forum/components/NotificationGrid', 'notificationTypes', function (items) {
    items.add('socialGroupNewPost', {
      name: 'socialGroupNewPost',
      icon: 'fa-solid fa-comment',
      label: app.translator.trans('ernestdefoe-social-groups.forum.settings.notify_social_group_new_post_label')
    });
    items.add('socialGroupNewReply', {
      name: 'socialGroupNewReply',
      icon: 'fa-solid fa-reply',
      label: app.translator.trans('ernestdefoe-social-groups.forum.settings.notify_social_group_new_reply_label')
    });
    items.add('socialGroupJoinRequest', {
      name: 'socialGroupJoinRequest',
      icon: 'fa-solid fa-user-plus',
      label: app.translator.trans('ernestdefoe-social-groups.forum.settings.notify_social_group_join_request_label')
    });
  });

  // The primary-group picker only shows on the member's own card and in
  // their settings, so it is fetched the first time one of those renders.
  let Picker = null;
  const PrimaryGroupSelector = {
    oninit() {
      if (!Picker) {
        import(/* webpackChunkName: 'forum/components/PrimaryGroupSelector', webpackMode: 'lazy-once' */ './forum/components/PrimaryGroupSelector').then(mod => {
          Picker = mod.default;
          m.redraw();
        });
      }
    },
    view: () => Picker ? m(Picker) : null
  };

  // Routes. Each page is its own chunk, fetched when the route is visited,
  // together with the shared page-styles chunk, so a visitor who never opens
  // a group pays nothing for the group UI. The styles are injected before the
  // page resolves, so it never renders unstyled.
  const page = load => () => Promise.all([load(), import(/* webpackChunkName: 'forum/pageStyles', webpackMode: 'lazy-once' */ './forum/pageStyles')]).then(([mod, styles]) => {
    styles.default();
    return mod;
  });
  app.routes['ernestdefoe-social-groups.index'] = {
    path: '/groups',
    component: page(() => import(/* webpackChunkName: 'forum/components/GroupsPage', webpackMode: 'lazy-once' */ './forum/components/GroupsPage'))
  };
  app.routes['ernestdefoe-social-groups.show'] = {
    path: '/groups/:slug',
    component: page(() => import(/* webpackChunkName: 'forum/components/GroupPage', webpackMode: 'lazy-once' */ './forum/components/GroupPage'))
  };
  app.routes['ernestdefoe-social-groups.discussion'] = {
    path: '/groups/:slug/d/:discussionId',
    component: page(() => import(/* webpackChunkName: 'forum/components/GroupDiscussionThread', webpackMode: 'lazy-once' */ './forum/components/GroupDiscussionThread'))
  };
  app.routes['user.socialGroups'] = {
    path: '/u/:username/groups',
    component: page(() => import(/* webpackChunkName: 'forum/components/GroupsUserPage', webpackMode: 'lazy-once' */ './forum/components/GroupsUserPage'))
  };

  // Groups item in the profile sidebar (below Posts/Discussions).
  flarumExtend(UserPage.prototype, 'navItems', function (items) {
    if (!this.user) return;
    items.add('socialGroups', m(LinkButton, {
      href: app.route('user.socialGroups', {
        username: this.user.slug()
      }),
      icon: 'fa-solid fa-users'
    }, app.translator.trans('ernestdefoe-social-groups.forum.profile_groups.nav')), 70);
  });

  // ── User card group badges + primary group selector ───────────────────────
  flarumExtend(UserCard.prototype, 'profileItems', function (items) {
    const user = this.attrs.user;
    if (!user || !user.id() || !items || typeof items.add !== 'function') return;

    // Always show group badges on every profile card
    items.add('social-group-badges', m(UserGroupBadges, {
      user,
      userId: user.id()
    }), -10);

    // Show the primary group selector only on the current user's own card
    if (app.session.user && String(app.session.user.id()) === String(user.id())) {
      items.add('sg-primary-group-selector', m(PrimaryGroupSelector), -20);
    }
  });

  // ── Primary group selector in account settings ────────────────────────────
  // 🚨 Extended BY PATH. SettingsPage is code-split in Flarum 2, so requiring
  // it at boot returned undefined, the guard skipped the extend, and the
  // selector silently never appeared. Core applies a path extend when the
  // chunk loads.
  flarumExtend('flarum/forum/components/SettingsPage', 'settingsItems', function (items) {
    if (app.session.user) {
      items.add('sg-primary-group', m(PrimaryGroupSelector), 10);
    }
  });

  // ── Primary group chip on posts (fof/badges forums) ──────────────────────
  // Forums running fof/badges show badge art beside post authors; the
  // author's primary social group joins that row as a small image chip.
  // Data rides on the serialized user (sgPrimaryGroup), so no extra
  // requests — and forums without fof/badges are untouched.
  if ('fof-badges' in flarum.extensions) {
    flarumExtend(CommentPost.prototype, 'headerItems', function (items) {
      const user = this.attrs.post?.user?.();
      const g = user?.attribute?.('sgPrimaryGroup');
      if (!g || !g.slug) return;
      items.add('sgPrimaryGroup', m(Link, {
        href: app.route('ernestdefoe-social-groups.show', {
          slug: g.slug
        }),
        className: 'SG-PostGroupChip',
        title: g.name
      }, [g.imageUrl ? m('img.SG-PostGroupChip-img', {
        src: g.imageUrl,
        alt: ''
      }) : m('span.SG-PostGroupChip-disc', {
        class: textContrastClass(g.color || '#4A90E2'),
        style: `background:${g.color || '#4A90E2'}`
      }, (g.name || '?')[0].toUpperCase()), m('span.SG-PostGroupChip-name', g.name)]), -5);
    });
  }

  // ── Sidebar navigation link ────────────────────────────────────────────────
  flarumExtend(IndexSidebar.prototype, 'navItems', function (items) {
    items.add('social-groups', m(LinkButton, {
      href: app.route('ernestdefoe-social-groups.index'),
      icon: 'fa-solid fa-users'
    }, app.translator.trans('ernestdefoe-social-groups.forum.groups.title')), 90);
  });
}, -10);flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/PrimaryGroupSelector', '644', 'ernestdefoe-social-groups', 'forum/components/PrimaryGroupSelector');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/pageStyles', '901', 'ernestdefoe-social-groups', 'forum/pageStyles');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupsPage', '537', 'ernestdefoe-social-groups', 'forum/components/GroupsPage');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/GroupPage');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupDiscussionThread', '659', 'ernestdefoe-social-groups', 'forum/components/GroupDiscussionThread');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupsUserPage', '8', 'ernestdefoe-social-groups', 'forum/components/GroupsUserPage');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/PrimaryGroupSelector', '644', 'ernestdefoe-social-groups', 'forum/utils/errors');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/pageStyles', '901', 'ernestdefoe-social-groups', 'forum/generated/pageStyles');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupsPage', '537', 'ernestdefoe-social-groups', 'forum/components/EditGroupModal');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupsPage', '537', 'ernestdefoe-social-groups', 'forum/components/ImageUploadButton');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupsPage', '537', 'ernestdefoe-social-groups', 'forum/components/SGSkeleton');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupsPage', '537', 'ernestdefoe-social-groups', 'forum/utils/colors');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupsPage', '537', 'ernestdefoe-social-groups', 'forum/utils/http');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupsPage', '537', 'ernestdefoe-social-groups', 'forum/utils/membership');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupsPage', '537', 'ernestdefoe-social-groups', 'forum/components/GroupCard');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupsPage', '537', 'ernestdefoe-social-groups', 'forum/components/CreateGroupModal');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/common/Avatar');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/common/EditForm');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/common/PostMenu');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/common/ReactionControls');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/feed/reactions');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/utils/reactions');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/common/ReplyComposer');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/utils/data');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/utils/linkPreview');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/utils/markdownToolbar');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/utils/popover');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/utils/uploads');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/GroupHero');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/GroupFeed');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/GroupMediaGallery');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/GroupAnalyticsPanel');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/MemberList');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/JoinRequestsPanel');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/feed/FeedComposer');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/feed/PostCard');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/common/Pagination');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/utils/MentionTracker');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/InviteUserModal');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/feed/PollComposer');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/feed/CommentList');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/feed/Poll');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/ShareDiscussionModal');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupPage', '912', 'ernestdefoe-social-groups', 'forum/components/feed/MentionDropdown');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupDiscussionThread', '659', 'ernestdefoe-social-groups', 'forum/components/thread/ThreadPost');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupDiscussionThread', '659', 'ernestdefoe-social-groups', 'forum/components/thread/ThreadReplyBox');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupDiscussionThread', '659', 'ernestdefoe-social-groups', 'forum/components/thread/TypingBar');
flarum.reg.addChunkModule('ernestdefoe-social-groups:forum/components/GroupDiscussionThread', '659', 'ernestdefoe-social-groups', 'forum/utils/realtime');